<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Model Household Evaluation</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Household Info -->
          <div class="card mb-4">
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="text-primary mb-3">Household Information</h6>
                  <p class="mb-1"><strong>Household Number:</strong> {{ household.household_number }}</p>
                  <p class="mb-1"><strong>Head of Household:</strong> {{ household.household_head_name || 'Not assigned' }}</p>
                  <p class="mb-1"><strong>Total Members:</strong> {{ household.member_count || 0 }}</p>
                  <p class="mb-0"><strong>CBHI Status:</strong>
                    <span v-if="household.cbhi_member_count > 0" class="badge bg-success">
                      Active ({{ household.cbhi_member_count }} members)
                    </span>
                    <span v-else class="badge bg-secondary">Non-CBHI</span>
                  </p>
                </div>
                <div class="col-md-6">
                  <h6 class="text-primary mb-3">Current Status</h6>
                  <div v-if="household.is_model_household" class="alert alert-success mb-0">
                    <i class="bx bx-check-circle"></i> Currently a Model Household
                    <p class="mb-0 mt-2">
                      <small>Last evaluation: {{ formatDate(household.last_evaluation_date) }}</small>
                    </p>
                  </div>
                  <div v-else class="alert alert-warning mb-0">
                    <i class="bx bx-info-circle"></i> Not yet certified as Model Household
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Evaluation Criteria -->
          <div class="card">
            <div class="card-header bg-light">
              <h6 class="mb-0">Model Household Criteria Evaluation</h6>
            </div>
            <div class="card-body">
              <!-- Progress Overview -->
              <div class="mb-4">
                <div class="d-flex justify-content-between mb-2">
                  <span>Overall Progress</span>
                  <strong>{{ criteriaMet }} / {{ totalCriteria }} criteria met</strong>
                </div>
                <div class="progress" style="height: 25px;">
                  <div class="progress-bar" role="progressbar"
                       :style="{width: progressPercentage + '%'}"
                       :class="progressBarClass">
                    {{ progressPercentage }}%
                  </div>
                </div>
                <p class="text-muted mt-2 mb-0">
                  <i class="bx bx-info-circle"></i>
                  Minimum {{ minimumCriteria }} out of {{ totalCriteria }} criteria required for certification
                </p>
              </div>

              <!-- Criteria Categories -->
              <div class="accordion" id="criteriaAccordion">
                <!-- 1. Sanitation & Hygiene -->
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button" type="button" data-bs-toggle="collapse"
                            data-bs-target="#sanitationCollapse">
                      <i class="bx bx-droplet me-2"></i>
                      Sanitation & Hygiene
                      <span class="ms-auto me-2">
                        <span class="badge" :class="getSectionBadgeClass('sanitation')">
                          {{ getSectionScore('sanitation') }}
                        </span>
                      </span>
                    </button>
                  </h2>
                  <div id="sanitationCollapse" class="accordion-collapse collapse show"
                       data-bs-parent="#criteriaAccordion">
                    <div class="accordion-body">
                      <div class="row g-3">
                        <!-- Latrine -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.has_latrine, 'border-danger': evaluation.has_latrine === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.has_latrine" id="hasLatrine">
                                <label class="form-check-label ms-2" for="hasLatrine">
                                  <strong>Functional Latrine</strong>
                                  <p class="mb-0 text-muted small">
                                    Household has access to improved latrine facility
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-home text-muted"></i>
                            </div>
                            <div v-if="evaluation.has_latrine" class="mt-2">
                              <select v-model="evaluation.latrine_type" class="form-select form-select-sm">
                                <option value="">Select type</option>
                                <option value="pit_latrine">Pit Latrine</option>
                                <option value="vip_latrine">VIP Latrine</option>
                                <option value="pour_flush">Pour Flush</option>
                                <option value="composting">Composting Toilet</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <!-- Handwashing -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.has_handwashing_facility, 'border-danger': evaluation.has_handwashing_facility === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.has_handwashing_facility" id="hasHandwashing">
                                <label class="form-check-label ms-2" for="hasHandwashing">
                                  <strong>Handwashing Facility</strong>
                                  <p class="mb-0 text-muted small">
                                    Handwashing station near latrine/kitchen
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-hand text-muted"></i>
                            </div>
                            <div v-if="evaluation.has_handwashing_facility" class="mt-2">
                              <div class="form-check form-check-inline">
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.soap_available" id="soapAvailable">
                                <label class="form-check-label" for="soapAvailable">
                                  Soap available
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Safe Water -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.safe_water_storage, 'border-danger': evaluation.safe_water_storage === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.safe_water_storage" id="safeWater">
                                <label class="form-check-label ms-2" for="safeWater">
                                  <strong>Safe Water Storage</strong>
                                  <p class="mb-0 text-muted small">
                                    Covered containers, proper water treatment
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-water text-muted"></i>
                            </div>
                            <div v-if="evaluation.safe_water_storage" class="mt-2">
                              <select v-model="evaluation.water_treatment_method" class="form-select form-select-sm">
                                <option value="">Treatment method</option>
                                <option value="boiling">Boiling</option>
                                <option value="chlorination">Chlorination</option>
                                <option value="filtering">Filtering</option>
                                <option value="none">No treatment</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        <!-- Waste Disposal -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.has_waste_disposal, 'border-danger': evaluation.has_waste_disposal === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.has_waste_disposal" id="wasteDisposal">
                                <label class="form-check-label ms-2" for="wasteDisposal">
                                  <strong>Proper Waste Disposal</strong>
                                  <p class="mb-0 text-muted small">
                                    Waste pit or proper disposal method
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-trash text-muted"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 2. Kitchen & Food Safety -->
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                            data-bs-target="#kitchenCollapse">
                      <i class="bx bx-restaurant me-2"></i>
                      Kitchen & Food Safety
                      <span class="ms-auto me-2">
                        <span class="badge" :class="getSectionBadgeClass('kitchen')">
                          {{ getSectionScore('kitchen') }}
                        </span>
                      </span>
                    </button>
                  </h2>
                  <div id="kitchenCollapse" class="accordion-collapse collapse"
                       data-bs-parent="#criteriaAccordion">
                    <div class="accordion-body">
                      <div class="row g-3">
                        <!-- Separate Kitchen -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.has_separate_kitchen, 'border-danger': evaluation.has_separate_kitchen === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.has_separate_kitchen" id="separateKitchen">
                                <label class="form-check-label ms-2" for="separateKitchen">
                                  <strong>Separate Kitchen</strong>
                                  <p class="mb-0 text-muted small">
                                    Kitchen separated from living areas
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-home-smile text-muted"></i>
                            </div>
                          </div>
                        </div>

                        <!-- Ventilation -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.kitchen_ventilation, 'border-danger': evaluation.kitchen_ventilation === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.kitchen_ventilation" id="kitchenVentilation">
                                <label class="form-check-label ms-2" for="kitchenVentilation">
                                  <strong>Proper Ventilation</strong>
                                  <p class="mb-0 text-muted small">
                                    Windows or chimney for smoke removal
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-wind text-muted"></i>
                            </div>
                          </div>
                        </div>

                        <!-- Food Storage -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.safe_food_storage, 'border-danger': evaluation.safe_food_storage === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.safe_food_storage" id="foodStorage">
                                <label class="form-check-label ms-2" for="foodStorage">
                                  <strong>Safe Food Storage</strong>
                                  <p class="mb-0 text-muted small">
                                    Covered containers, elevated storage
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-package text-muted"></i>
                            </div>
                          </div>
                        </div>

                        <!-- Clean Cooking -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.clean_cooking_area, 'border-danger': evaluation.clean_cooking_area === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.clean_cooking_area" id="cleanCooking">
                                <label class="form-check-label ms-2" for="cleanCooking">
                                  <strong>Clean Cooking Area</strong>
                                  <p class="mb-0 text-muted small">
                                    Clean surfaces and utensils
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-check-shield text-muted"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 3. Health Practices -->
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                            data-bs-target="#healthCollapse">
                      <i class="bx bx-health me-2"></i>
                      Health Practices
                      <span class="ms-auto me-2">
                        <span class="badge" :class="getSectionBadgeClass('health')">
                          {{ getSectionScore('health') }}
                        </span>
                      </span>
                    </button>
                  </h2>
                  <div id="healthCollapse" class="accordion-collapse collapse"
                       data-bs-parent="#criteriaAccordion">
                    <div class="accordion-body">
                      <div class="row g-3">
                        <!-- Immunization -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.children_immunized, 'border-danger': evaluation.children_immunized === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.children_immunized" id="childrenImmunized">
                                <label class="form-check-label ms-2" for="childrenImmunized">
                                  <strong>Children Fully Immunized</strong>
                                  <p class="mb-0 text-muted small">
                                    All children up-to-date with vaccinations
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-injection text-muted"></i>
                            </div>
                          </div>
                        </div>

                        <!-- ANC Compliance -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.anc_compliance, 'border-danger': evaluation.anc_compliance === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.anc_compliance" id="ancCompliance">
                                <label class="form-check-label ms-2" for="ancCompliance">
                                  <strong>ANC Compliance</strong>
                                  <p class="mb-0 text-muted small">
                                    Pregnant women attending ANC visits
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-female text-muted"></i>
                            </div>
                          </div>
                        </div>

                        <!-- Family Planning -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.family_planning, 'border-danger': evaluation.family_planning === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.family_planning" id="familyPlanning">
                                <label class="form-check-label ms-2" for="familyPlanning">
                                  <strong>Family Planning Use</strong>
                                  <p class="mb-0 text-muted small">
                                    Appropriate family planning methods
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-group text-muted"></i>
                            </div>
                          </div>
                        </div>

                        <!-- Health Insurance -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.cbhi_enrolled, 'border-danger': evaluation.cbhi_enrolled === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.cbhi_enrolled" id="cbhiEnrolled">
                                <label class="form-check-label ms-2" for="cbhiEnrolled">
                                  <strong>CBHI Enrollment</strong>
                                  <p class="mb-0 text-muted small">
                                    Active CBHI membership
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-shield-plus text-muted"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 4. Environmental Health -->
                <div class="accordion-item">
                  <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                            data-bs-target="#environmentCollapse">
                      <i class="bx bx-leaf me-2"></i>
                      Environmental Health
                      <span class="ms-auto me-2">
                        <span class="badge" :class="getSectionBadgeClass('environment')">
                          {{ getSectionScore('environment') }}
                        </span>
                      </span>
                    </button>
                  </h2>
                  <div id="environmentCollapse" class="accordion-collapse collapse"
                       data-bs-parent="#criteriaAccordion">
                    <div class="accordion-body">
                      <div class="row g-3">
                        <!-- Clean Compound -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.clean_compound, 'border-danger': evaluation.clean_compound === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.clean_compound" id="cleanCompound">
                                <label class="form-check-label ms-2" for="cleanCompound">
                                  <strong>Clean Compound</strong>
                                  <p class="mb-0 text-muted small">
                                    No stagnant water or waste
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-home-circle text-muted"></i>
                            </div>
                          </div>
                        </div>

                        <!-- Animal Management -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.animal_management, 'border-danger': evaluation.animal_management === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.animal_management" id="animalManagement">
                                <label class="form-check-label ms-2" for="animalManagement">
                                  <strong>Proper Animal Management</strong>
                                  <p class="mb-0 text-muted small">
                                    Animals kept away from living areas
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-bug text-muted"></i>
                            </div>
                          </div>
                        </div>

                        <!-- Vector Control -->
                        <div class="col-md-6">
                          <div class="form-check card p-3"
                               :class="{'border-success': evaluation.vector_control, 'border-danger': evaluation.vector_control === false}">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <input class="form-check-input" type="checkbox"
                                       v-model="evaluation.vector_control" id="vectorControl">
                                <label class="form-check-label ms-2" for="vectorControl">
                                  <strong>Vector Control</strong>
                                  <p class="mb-0 text-muted small">
                                    No mosquito breeding sites
                                  </p>
                                </label>
                              </div>
                              <i class="bx bx-shield-x text-muted"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Additional Notes -->
              <div class="mt-4">
                <label class="form-label">Additional Notes</label>
                <textarea v-model="evaluation.notes" class="form-control" rows="3"
                          placeholder="Any additional observations or recommendations..."></textarea>
              </div>

              <!-- Recommendation -->
              <div class="mt-4">
                <div class="alert" :class="recommendationAlertClass">
                  <h6 class="alert-heading">
                    <i :class="recommendationIcon"></i> {{ recommendationTitle }}
                  </h6>
                  <p class="mb-0">{{ recommendationText }}</p>
                  <div v-if="!qualifiesAsModel" class="mt-3">
                    <strong>Missing criteria:</strong>
                    <ul class="mb-0 mt-2">
                      <li v-for="criteria in missingCriteria" :key="criteria">
                        {{ criteria }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="button" class="btn btn-warning" @click="saveDraft" :disabled="submitting">
            <i class="bx bx-save"></i> Save Draft
          </button>
          <button type="button" class="btn"
                  :class="qualifiesAsModel ? 'btn-success' : 'btn-primary'"
                  @click="submitEvaluation" :disabled="submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Submitting...
            </span>
            <span v-else>
              <i class="bx bx-check"></i> Submit Evaluation
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ModelHouseholdEvaluationModal",
  props: {
    household: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      submitting: false,

      // Evaluation criteria
      evaluation: {
        // Sanitation & Hygiene
        has_latrine: null,
        latrine_type: '',
        has_handwashing_facility: null,
        soap_available: false,
        safe_water_storage: null,
        water_treatment_method: '',
        has_waste_disposal: null,

        // Kitchen & Food Safety
        has_separate_kitchen: null,
        kitchen_ventilation: null,
        safe_food_storage: null,
        clean_cooking_area: null,

        // Health Practices
        children_immunized: null,
        anc_compliance: null,
        family_planning: null,
        cbhi_enrolled: null,

        // Environmental Health
        clean_compound: null,
        animal_management: null,
        vector_control: null,

        // Additional
        notes: ''
      },

      // Configuration
      totalCriteria: 15,
      minimumCriteria: 12 // 80% required
    };
  },

  computed: {
    criteriaMet() {
      let count = 0;
      const criteriaFields = [
        'has_latrine', 'has_handwashing_facility', 'safe_water_storage', 'has_waste_disposal',
        'has_separate_kitchen', 'kitchen_ventilation', 'safe_food_storage', 'clean_cooking_area',
        'children_immunized', 'anc_compliance', 'family_planning', 'cbhi_enrolled',
        'clean_compound', 'animal_management', 'vector_control'
      ];

      criteriaFields.forEach(field => {
        if (this.evaluation[field] === true) count++;
      });

      return count;
    },

    progressPercentage() {
      return Math.round((this.criteriaMet / this.totalCriteria) * 100);
    },

    progressBarClass() {
      if (this.progressPercentage >= 80) return 'bg-success';
      if (this.progressPercentage >= 60) return 'bg-warning';
      return 'bg-danger';
    },

    qualifiesAsModel() {
      return this.criteriaMet >= this.minimumCriteria;
    },

    missingCriteria() {
      const missing = [];
      const criteriaMap = {
        'has_latrine': 'Functional Latrine',
        'has_handwashing_facility': 'Handwashing Facility',
        'safe_water_storage': 'Safe Water Storage',
        'has_waste_disposal': 'Proper Waste Disposal',
        'has_separate_kitchen': 'Separate Kitchen',
        'kitchen_ventilation': 'Proper Kitchen Ventilation',
        'safe_food_storage': 'Safe Food Storage',
        'clean_cooking_area': 'Clean Cooking Area',
        'children_immunized': 'Children Fully Immunized',
        'anc_compliance': 'ANC Compliance',
        'family_planning': 'Family Planning Use',
        'cbhi_enrolled': 'CBHI Enrollment',
        'clean_compound': 'Clean Compound',
        'animal_management': 'Proper Animal Management',
        'vector_control': 'Vector Control'
      };

      Object.entries(criteriaMap).forEach(([field, label]) => {
        if (this.evaluation[field] !== true) {
          missing.push(label);
        }
      });

      return missing;
    },

    recommendationAlertClass() {
      return this.qualifiesAsModel ? 'alert-success' : 'alert-warning';
    },

    recommendationIcon() {
      return this.qualifiesAsModel ? 'bx bx-check-circle' : 'bx bx-info-circle';
    },

    recommendationTitle() {
      return this.qualifiesAsModel ? 'Qualifies for Model Household Certification' : 'Does Not Yet Qualify';
    },

    recommendationText() {
      if (this.qualifiesAsModel) {
        return `This household meets ${this.criteriaMet} out of ${this.totalCriteria} criteria and qualifies for Model Household certification.`;
      } else {
        return `This household meets only ${this.criteriaMet} out of ${this.totalCriteria} criteria. At least ${this.minimumCriteria} criteria must be met for certification.`;
      }
    }
  },

  mounted() {
    this.loadPreviousEvaluation();

    // Set CBHI status from household data
    if (this.household.cbhi_member_count > 0) {
      this.evaluation.cbhi_enrolled = true;
    }
  },

  methods: {
    async loadPreviousEvaluation() {
      // Load any previous evaluation data if exists
      const data = {
        "function": "getModelHouseholdEvaluation",
        "householdId": this.household.household_id
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success && res.data.data.evaluation) {
          // Populate form with previous evaluation
          Object.assign(this.evaluation, res.data.data.evaluation);
        }
      } catch (e) {
        console.error("Failed to load previous evaluation:", e);
      }
    },

    getSectionScore(section) {
      let met = 0;
      let total = 0;

      const sectionCriteria = {
        'sanitation': ['has_latrine', 'has_handwashing_facility', 'safe_water_storage', 'has_waste_disposal'],
        'kitchen': ['has_separate_kitchen', 'kitchen_ventilation', 'safe_food_storage', 'clean_cooking_area'],
        'health': ['children_immunized', 'anc_compliance', 'family_planning', 'cbhi_enrolled'],
        'environment': ['clean_compound', 'animal_management', 'vector_control']
      };

      sectionCriteria[section].forEach(field => {
        total++;
        if (this.evaluation[field] === true) met++;
      });

      return `${met}/${total}`;
    },

    getSectionBadgeClass(section) {
      const score = this.getSectionScore(section);
      const [met, total] = score.split('/').map(Number);
      const percentage = (met / total) * 100;

      if (percentage >= 75) return 'bg-success';
      if (percentage >= 50) return 'bg-warning';
      return 'bg-danger';
    },

    async saveDraft() {
      this.submitting = true;

      const data = {
        "function": "saveModelHouseholdEvaluation",
        "householdId": this.household.household_id,
        "evaluation": this.evaluation,
        "isDraft": true
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('saved', { isDraft: true });
        } else {
          alert(res.data.message || 'Failed to save draft');
        }
      } catch (e) {
        console.error("Failed to save draft:", e);
        alert('Failed to save draft. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    async submitEvaluation() {
      // Validate all criteria have been evaluated
      const unevaluated = [];
      const criteriaFields = [
        'has_latrine', 'has_handwashing_facility', 'safe_water_storage', 'has_waste_disposal',
        'has_separate_kitchen', 'kitchen_ventilation', 'safe_food_storage', 'clean_cooking_area',
        'children_immunized', 'anc_compliance', 'family_planning', 'cbhi_enrolled',
        'clean_compound', 'animal_management', 'vector_control'
      ];

      criteriaFields.forEach(field => {
        if (this.evaluation[field] === null) {
          unevaluated.push(field);
        }
      });

      if (unevaluated.length > 0) {
        alert('Please evaluate all criteria before submitting.');
        return;
      }

      this.submitting = true;

      const data = {
        "function": "submitModelHouseholdEvaluation",
        "householdId": this.household.household_id,
        "evaluation": this.evaluation,
        "qualifies": this.qualifiesAsModel,
        "criteriaMet": this.criteriaMet,
        "totalCriteria": this.totalCriteria
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('evaluated', {
            qualifies: this.qualifiesAsModel,
            criteriaMet: this.criteriaMet
          });
        } else {
          alert(res.data.message || 'Failed to submit evaluation');
        }
      } catch (e) {
        console.error("Failed to submit evaluation:", e);
        alert('Failed to submit evaluation. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'Never evaluated';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.modal-dialog-scrollable .modal-body {
  max-height: calc(100vh - 200px);
}

.form-check.card {
  cursor: pointer;
  transition: all 0.2s;
}

.form-check.card:hover {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.form-check.card.border-success {
  background-color: rgba(25, 135, 84, 0.05);
}

.form-check.card.border-danger {
  background-color: rgba(220, 53, 69, 0.05);
}

.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
}

.progress {
  background-color: #e9ecef;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}
</style>